import {nest} from "d3-collection";
import React, {Component} from "react";
import PropTypes from "prop-types";
import GenericChartComponent from "react-stockcharts/lib/GenericChartComponent";
import {getAxisCanvas} from "react-stockcharts/lib/GenericComponent";
import {isDefined, functor} from "react-stockcharts/lib/utils";
import {formatDate, formatShortDateWithTime} from "../../config";

class OHLCSeries extends Component {
    constructor(props) {
        super(props);
        this.drawOnCanvas = this.drawOnCanvas.bind(this);
        this.orderColors = {
            buyCloseOrderColor: this.props.buyCloseOrderColor,
            buyCloseOrderBorderColor: this.props.buyCloseOrderBorderColor,
            sellCloseOrderColor: this.props.sellCloseOrderColor,
            sellCloseOrderBorderColor: this.props.sellCloseOrderBorderColor,
            buyOpenOrderColor: this.props.buyOpenOrderColor,
            buyOpenOrderBorderColor: this.props.buyOpenOrderBorderColor,
            sellOpenOrderColor: this.props.sellOpenOrderColor,
            sellOpenOrderBorderColor: this.props.sellOpenOrderBorderColor,
        };
    }

    drawOnCanvas(ctx, moreProps) {
        const {yAccessor} = this.props;
        const {xAccessor} = moreProps;
        const {xScale, chartConfig: {yScale}, plotData} = moreProps;

        const barData = getOHLCBars(this.props, xAccessor, yAccessor, xScale, yScale, plotData);

        let showClosedOrders = this.props.showClosedOrders;
        let showOpenedOrders = this.props.showOpenedOrders;

        drawOnCanvas(ctx, barData, barData.openOrdersLines, showClosedOrders, showOpenedOrders, this.orderColors, this.props.showCanceledOrders);

    }

    render() {
        const {clip} = this.props;

        return <GenericChartComponent
            canvasToDraw={getAxisCanvas}
            canvasDraw={this.drawOnCanvas}
            clip={clip}
            drawOn={["pan"]}
        />;
    }
}

OHLCSeries.propTypes = {
    className: PropTypes.string,
    classNames: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string
    ]),
    stroke: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string
    ]).isRequired,
    yAccessor: PropTypes.func,
    clip: PropTypes.bool,
};

OHLCSeries.defaultProps = {
    className: "react-stockcharts-ohlc",
    yAccessor: (d) => ({open: d.open, high: d.high, low: d.low, close: d.close}),
    classNames: d => isDefined(d.absoluteChange) ? (d.absoluteChange > 0 ? "up" : "down") : "firstbar",
    stroke: d => isDefined(d.absoluteChange) ? (d.absoluteChange > 0 ? "#6BA583" : "#FF0000") : "#000000",
    clip: true,
};

function drawOnCanvas(ctx, barData, openOrdersLines, showClosedOrders, showOpenedOrders, colors, showCanceledOrders) {

    const {strokeWidth, bars} = barData;

    const wickNest = nest()
        .key(d => d.stroke)
        .entries(bars);

    ctx.lineWidth = strokeWidth;

    wickNest.forEach(outer => {
        const {key, values} = outer;
        ctx.strokeStyle = key;
        values.forEach(d => {
            ctx.beginPath();
            ctx.moveTo(d.x, d.y1);
            ctx.lineTo(d.x, d.y2);

            ctx.moveTo(d.openX1, d.openY);
            ctx.lineTo(d.openX2, d.openY);

            ctx.moveTo(d.closeX1, d.closeY);
            ctx.lineTo(d.closeX2, d.closeY);

            ctx.stroke();
        });
    });

    let barsWithOrders = bars.filter(bar => bar.orders.length > 0);

    if (showClosedOrders) {

        bars.forEach(bar => {
            bar.orders.forEach(order => {

                if (showCanceledOrders) {
                    if (order.status === 'FILLED' || order.status === 'CANCELED' || order.status === 'PENDING_CANCEL' || order.status === 'REJECTED' || order.status === 'EXPIRED') {
                        drowOrder();
                    }
                } else {
                    if (order.status === 'FILLED') {
                        drowOrder();
                    }
                }

                function drowOrder() {
                    let width = 10;
                    let x = order.x1 + ((order.x2 - order.x1) / 2) - (width / 2);
                    let y = order.y;
                    let type = order.type;

                    if (type === 'SELL') {
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x + width, y);
                        ctx.lineTo(x + width / 2, y + width);
                        ctx.lineTo(x, y);
                        ctx.closePath();
                        ctx.strokeStyle = colors.sellCloseOrderBorderColor;
                        ctx.stroke();
                        ctx.fillStyle = colors.sellCloseOrderColor;
                        ctx.fill();
                    } else {
                        ctx.beginPath();

                        ctx.moveTo(x, y);
                        ctx.lineTo(x + width, y);
                        ctx.lineTo(x + width / 2, y - width);
                        ctx.lineTo(x, y);
                        ctx.closePath();
                        ctx.strokeStyle = colors.buyCloseOrderBorderColor;
                        ctx.stroke();
                        ctx.fillStyle = colors.buyCloseOrderColor;
                        ctx.fill();
                    }
                }

            });
        });
    }

    if (showOpenedOrders) {
        openOrdersLines.forEach(order => {
            // let width = order.x2 - order.x1;
            let x = order.x;
            let y = order.y;
            let markerHeight = 20;
            let type = order.type;
            let price = order.price;
            let borderColor;
            let fillColor;

            if (type === 'sell') {
                borderColor = colors.sellOpenOrderBorderColor;
                fillColor = colors.sellOpenOrderColor;
            } else {
                borderColor = colors.buyOpenOrderBorderColor;
                fillColor = colors.buyOpenOrderColor;
            }

            ctx.beginPath();
            ctx.strokeStyle = borderColor;
            ctx.moveTo(0, y);
            ctx.lineWidth = 2;
            ctx.lineTo(1200, y);
            ctx.stroke();
            ctx.fillStyle = fillColor;
            ctx.strokeRect(14, y - ((markerHeight + 2) / 2), 162, (markerHeight + 2));
            ctx.fillRect(15, y - (markerHeight / 2), 160, markerHeight);
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#fff';
            ctx.font = "bold 14px Arial";
            ctx.font = "medium 14px Arial";
            ctx.fillText(`${price}`, 24, y);
            ctx.font = "14px Arial";
            ctx.fillText(`${formatShortDateWithTime(order.time)}`, 97, y);
        });
    }


}

function getOHLCBars(props, xAccessor, yAccessor, xScale, yScale, plotData) {
    const {classNames: classNamesProp, stroke: strokeProp} = props;

    const strokeFunc = functor(strokeProp);
    const classNameFunc = functor(classNamesProp);

    const width = xScale(xAccessor(plotData[plotData.length - 1]))
        - xScale(xAccessor(plotData[0]));

    const barWidth = Math.max(1, Math.round(width / (plotData.length - 1) / 2) - 1.5);
    const strokeWidth = Math.min(barWidth, 6);

    const bars = plotData
        .filter(d => isDefined(yAccessor(d).close))
        .map(d => {
            const ohlc = yAccessor(d),
                x = Math.round(xScale(xAccessor(d))),
                y1 = yScale(ohlc.high),
                y2 = yScale(ohlc.low),
                openX1 = x - barWidth,
                openX2 = x + strokeWidth / 2,
                openY = yScale(ohlc.open),
                closeX1 = x - strokeWidth / 2,
                closeX2 = x + barWidth,
                closeY = yScale(ohlc.close),
                className = classNameFunc(d),
                stroke = strokeFunc(d);

            let orders = [];

            if (d.orders.length > 0) {
                const x1 = openX1;
                const x2 = closeX2;

                orders = d.orders.map(order => {
                    const y = yScale(order.price);
                    const type = order.type;
                    const status = order.status;
                    return {x1, x2, y, type, x, status};
                });
            }

            return {x, y1, y2, openX1, openX2, openY, closeX1, closeX2, closeY, stroke, className, orders};
        });

    const openOrders = props.openOrders;
    const openOrdersLines = openOrders.map(order => {


        let x = Math.round(xScale(xAccessor(order)));
        let y = yScale(order.price);
        let price = order.price;
        let type = order.type;
        let time = order.open_time;

        return {x, y, price, type, time};
    });

    return {barWidth, strokeWidth, bars, openOrdersLines};
}

export default OHLCSeries;
